/**
 * JourneyStepper — the one consistent "where am I in the journey" strip for
 * the invite → account → profile → workspace flow (2026-08 owner directive:
 * every step shows what's happening, how far along the user is, and how long
 * it takes, in the official lowercase prest0 style).
 *
 * Rendered on the pricing/start account-question panel (step 2) and on the
 * trial onboarding page (steps 3–4) so the numbering never changes shape
 * between surfaces.
 */

import React from 'react';
import styled, { css } from 'styled-components';
import { media } from '../../styles/breakpoints';

// The journey's steps are plain data in src/config/newAccountJourney.ts (2026-09-30), so the pricing page's rebuild in
// the new design reads the same list without importing this styled file. Re-exported here for every existing caller.
import { NEW_ACCOUNT_JOURNEY, journeyCounter, type JourneyStep } from '../../config/newAccountJourney';
export { NEW_ACCOUNT_JOURNEY };
export type { JourneyStep };

/** How long the workstation takes: one source (src/config/workstationEstimates.ts), re-exported for today's importers. */
export { WORKSTATION_BOOT_ESTIMATE, WORKSTATION_WAKE_ESTIMATE } from '../../config/workstationEstimates';

const Strip = styled.div`
  background: #fff;
  border: 1px solid #E6E4DE;
  border-radius: 1rem;
  padding: 0.85rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  /* Wrap pass (2026-08-09): the strip measures ITSELF so the horizontal
     layout can collapse gracefully in narrow containers (the ~550px
     /pricing column, phone widths) — six nowrap labels used to wrap
     mid-flow leaving stretched connector stubs. */
  container-type: inline-size;

  ${media.laptop} {
    padding: 1.1rem 1.4rem;
    gap: 0.75rem;
  }
`;

const Counter = styled.div`
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: lowercase;
  color: var(--brand-text-muted);
  font-weight: 600;

  ${media.laptop} {
    font-size: 0.78rem;
  }
`;

const Row = styled.div<{ $rail?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  flex-wrap: wrap;

  ${p => p.$rail && css`
    ${media.laptop} {
      flex-direction: column;
      align-items: stretch;
      gap: 0.4rem;
    }
  `}
`;

const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;

  ${media.laptop} {
    gap: 0.55rem;
  }
`;

const Dot = styled.div<{ $state: 'done' | 'active' | 'upcoming' }>`
  width: 20px;
  height: 20px;
  border-radius: 999px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.62rem;
  font-weight: 700;
  background: ${p => p.$state === 'done' ? '#27AE60' : p.$state === 'active' ? 'var(--brand-text)' : '#EDEAE4'};
  color: ${p => p.$state === 'upcoming' ? 'var(--brand-text-muted)' : '#fff'};

  ${media.laptop} {
    width: 26px;
    height: 26px;
    font-size: 0.72rem;
  }
`;

const StepText = styled.div<{ $state: 'done' | 'active' | 'upcoming'; $rail?: boolean }>`
  font-size: 0.78rem;
  font-weight: ${p => p.$state === 'active' ? 700 : 500};
  color: ${p => p.$state === 'active' ? 'var(--brand-text)' : p.$state === 'done' ? '#4B7B5D' : 'var(--brand-text-muted)'};
  text-transform: lowercase;
  white-space: nowrap;

  /* Compact collapse: when the strip is too narrow for six labels, keep the
     dots + the ACTIVE label only — position stays legible, nothing wraps. */
  @container (max-width: 620px) {
    ${p => p.$state !== 'active' && css`display: none;`}
  }

  ${media.laptop} {
    font-size: 0.9rem;
  }

  /* The desktop side-rail is vertical — every label always shows there. */
  ${p => p.$rail && css`
    ${media.laptop} {
      display: block;
    }
  `}
`;

const Connector = styled.div<{ $done: boolean; $rail?: boolean }>`
  flex: 1 1 12px;
  min-width: 12px;
  height: 2px;
  border-radius: 2px;
  margin: 9px 0.1rem 0;
  background: ${p => p.$done ? '#27AE60' : '#EDEAE4'};
  align-self: flex-start;

  /* Compact collapse: fixed-length connectors so a tight container never
     shows stretched stubs between wrapped rows. */
  @container (max-width: 620px) {
    flex: 0 0 10px;
    min-width: 10px;
  }

  ${media.laptop} {
    margin-top: 12px;
  }

  ${p => p.$rail && css`
    ${media.laptop} {
      flex: 0 0 auto;
      min-width: 0;
      width: 2px;
      height: 14px;
      margin: 0 0 0 12px;
      align-self: auto;
    }
  `}
`;

const Hint = styled.div`
  font-size: 0.74rem;
  color: var(--brand-text-muted);
  text-transform: lowercase;
  /* Progressive enhancement — avoids orphaning the last word in 300px rails. */
  text-wrap: pretty;

  ${media.laptop} {
    font-size: 0.82rem;
  }
`;

const HintTime = styled.span`
  white-space: nowrap;
`;

export interface JourneyStepperProps {
  steps?: JourneyStep[];
  /** Key of the step the user is on right now. */
  currentKey: string;
  /**
   * At ≥1024px, stack the steps vertically for a narrow side-rail placement
   * (mobile keeps the horizontal strip). CSS-only — same DOM, same numbering.
   */
  railOnDesktop?: boolean;
  /**
   * Free stage navigation (task #56): steps whose key is listed here render
   * as buttons — clicking one calls onStepClick. The owner's contract:
   * moving between stages must be easy and never leave onboarding, so the
   * strip itself is the navigator. Steps not listed stay inert (e.g. the
   * account-creation steps that live on other pages, or the current step).
   */
  navigableKeys?: string[];
  onStepClick?: (key: string) => void;
}

export const JourneyStepper: React.FC<JourneyStepperProps> = ({
  steps = NEW_ACCOUNT_JOURNEY,
  currentKey,
  railOnDesktop = false,
  navigableKeys,
  onStepClick,
}) => {
  const currentIndex = Math.max(0, steps.findIndex(s => s.key === currentKey));
  const current = steps[currentIndex];

  return (
    <Strip data-testid="journey-stepper" aria-label={journeyCounter(currentIndex + 1, steps.length)}>
      <Counter>{journeyCounter(currentIndex + 1, steps.length)}</Counter>
      <Row $rail={railOnDesktop}>
        {steps.map((step, i) => {
          const state: 'done' | 'active' | 'upcoming' =
            i < currentIndex ? 'done' : i === currentIndex ? 'active' : 'upcoming';
          return (
            <React.Fragment key={step.key}>
              {i > 0 && <Connector $done={i <= currentIndex} $rail={railOnDesktop} />}
              {navigableKeys?.includes(step.key) && onStepClick && step.key !== currentKey ? (
                <StepItem
                  as="button"
                  type="button"
                  onClick={() => onStepClick(step.key)}
                  title={`go to ${step.label}`}
                  style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}
                >
                  <Dot $state={state}>{state === 'done' ? '✓' : i + 1}</Dot>
                  <StepText $state={state} $rail={railOnDesktop} style={{ textDecoration: 'underline', textUnderlineOffset: '3px' }}>
                    {step.label}
                  </StepText>
                </StepItem>
              ) : (
                <StepItem>
                  <Dot $state={state}>{state === 'done' ? '✓' : i + 1}</Dot>
                  <StepText $state={state} $rail={railOnDesktop}>{step.label}</StepText>
                </StepItem>
              )}
            </React.Fragment>
          );
        })}
      </Row>
      {current?.timeHint && (
        <Hint>{current.label} — <HintTime>{current.timeHint}</HintTime></Hint>
      )}
    </Strip>
  );
};

export default JourneyStepper;
